import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useSite } from '../content/SiteContext';

// Componente para contador animado simple y funcional
const AnimatedCounter = ({ end, duration = 2, delay = 0, suffix = "", prefix = "" }) => {
  const [count, setCount] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const countRef = useRef(null);
  const isInView = useInView(countRef, { once: true, amount: 0.1 });

  useEffect(() => {
    if (!isInView) return;

    // Delay inicial
    const startTimer = setTimeout(() => {
      setIsAnimating(true);
      let currentCount = 0;
      const increment = end / (duration * 30); // 30 FPS para compatibilidad
      
      const interval = setInterval(() => {
        currentCount += increment;
        
        if (currentCount >= end) {
          setCount(end);
          clearInterval(interval);
        } else {
          setCount(Math.floor(currentCount));
        }
      }, 1000 / 30);

      return () => clearInterval(interval);
    }, delay * 1000);

    return () => clearTimeout(startTimer);
  }, [isInView, end, duration, delay]);

  // Fallback: mostrar el número final si no se está animando
  if (!isAnimating) {
    return (
      <span ref={countRef} className="inline-block">
        {prefix}{end}{suffix}
      </span>
    );
  }

  return (
    <span ref={countRef} className="inline-block">
      {prefix}{count}{suffix}
    </span>
  );
};

const BrandsSection = () => {
  const { content, t } = useSite();
  const section = content.brands;
  const clients1 = section.row1 || [];
  const clients2 = section.row2 || [];
  const [isDragging1, setIsDragging1] = useState(false);
  const [isDragging2, setIsDragging2] = useState(false);
  const [currentPosition1, setCurrentPosition1] = useState(0);
  const [currentPosition2, setCurrentPosition2] = useState(0);
  const [isAnimating1, setIsAnimating1] = useState(true);
  const [isAnimating2, setIsAnimating2] = useState(true);
  const [startX1, setStartX1] = useState(0);
  const [startX2, setStartX2] = useState(0);
  const [scrollLeft1, setScrollLeft1] = useState(0);
  const [scrollLeft2, setScrollLeft2] = useState(0);

  // Velocidad de animación (píxeles por segundo)
  const animationSpeed = 40;

  // Duplicar clientes para efecto infinito
  const duplicatedClients1 = [...clients1, ...clients1];
  const duplicatedClients2 = [...clients2, ...clients2];

  // Calcular el ancho total del contenido duplicado
  const totalWidth1 = duplicatedClients1.length * 160;
  const totalWidth2 = duplicatedClients2.length * 160;

  useEffect(() => {
    let animationId;
    let lastTime = Date.now();

    const animate = () => {
      if (!isDragging1 && isAnimating1) {
        const currentTime = Date.now();
        const deltaTime = currentTime - lastTime;
        const deltaPosition = (animationSpeed * deltaTime) / 1000;

        setCurrentPosition1(prev => {
          let newPosition = prev - deltaPosition;

          // Si llega al final, reiniciar a una posición intermedia para crear el efecto infinito
          if (newPosition <= -totalWidth1 / 2) {
            newPosition = 0;
          }
          return newPosition;
        });
      }

      if (!isDragging2 && isAnimating2) {
        const currentTime = Date.now();
        const deltaTime = currentTime - lastTime;
        const deltaPosition = (animationSpeed * deltaTime) / 1000;

        setCurrentPosition2(prev => {
          let newPosition = prev - deltaPosition;

          // Si llega al final, reiniciar a una posición intermedia para crear el efecto infinito
          if (newPosition <= -totalWidth2 / 2) {
            newPosition = 0;
          }
          return newPosition;
        });
      }

      lastTime = Date.now();
      animationId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [isDragging1, isDragging2, isAnimating1, isAnimating2, totalWidth1, totalWidth2]);

  const handleDragStart1 = (event) => {
    setIsDragging1(true);
    setIsAnimating1(false);
    setStartX1(event.pageX || event.touches[0].pageX);
    setScrollLeft1(currentPosition1);
  };

  const handleDragMove1 = (event) => {
    if (!isDragging1) return;
    const x = event.pageX || event.touches[0].pageX;
    const walk = (x - startX1) * 2; // Ajuste de velocidad de desplazamiento
    let newPosition = scrollLeft1 + walk;

    // Asegurar que el desplazamiento no se salga de los límites del contenido duplicado
    if (newPosition > 0) newPosition = 0;
    if (newPosition < -totalWidth1 / 2) newPosition = -totalWidth1 / 2;

    setCurrentPosition1(newPosition);
  };

  const handleDragEnd1 = () => {
    setIsDragging1(false);
    setIsAnimating1(true);
  };

  const handleDragStart2 = (event) => {
    setIsDragging2(true);
    setIsAnimating2(false);
    setStartX2(event.pageX || event.touches[0].pageX);
    setScrollLeft2(currentPosition2);
  };

  const handleDragMove2 = (event) => {
    if (!isDragging2) return;
    const x = event.pageX || event.touches[0].pageX;
    const walk = (x - startX2) * 2; // Ajuste de velocidad de desplazamiento
    let newPosition = scrollLeft2 + walk;

    // Asegurar que el desplazamiento no se salga de los límites del contenido duplicado
    if (newPosition > 0) newPosition = 0;
    if (newPosition < -totalWidth2 / 2) newPosition = -totalWidth2 / 2;

    setCurrentPosition2(newPosition);
  };

  const handleDragEnd2 = () => {
    setIsDragging2(false);
    setIsAnimating2(true);
  };

  return (
    <section id="clientes" className="py-16 md:py-24 bg-gradient-to-br from-gray-900 via-gray-800 to-black overflow-hidden">
      <div className="container mx-auto px-4">
        <motion.h3
          className="section-heading text-white reveal text-center"
          style={{
            fontSize: 'clamp(2rem, 6vw, 2.5rem)',
            fontWeight: 700,
            marginBottom: '1rem',
            fontFamily: 'Inter, sans-serif'
          }}
          initial={{ opacity: 0, y: -50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          {t(section.title1)}{' '}
          <span
            className="text-gradient"
            style={{
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent',
              backgroundImage: 'linear-gradient(to right, #a78bfa, #f472b6)'
            }}
          >
            {t(section.title2)}
          </span>
        </motion.h3>
        <motion.p
          className="text-center max-w-3xl mx-auto text-gray-300 mt-4 md:mt-6 mb-12 md:mb-16 text-base md:text-lg px-4"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.8, ease: "easeOut", delay: 0.3 }}
        >
          {t(section.subtitle)}
        </motion.p>
      </div>
      <div className="relative">
        {/* Primer carrusel */}
        <motion.div
          className="mb-12 md:mb-16"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 1, ease: "easeOut" }}
        >
          <div className="overflow-hidden">
            <motion.div
              className="flex space-x-4 md:space-x-8 cursor-grab active:cursor-grabbing"
              style={{
                width: 'max-content',
                transform: `translateX(${currentPosition1}px)`,
                userSelect: 'none',
                touchAction: 'pan-y'
              }}
              onMouseDown={handleDragStart1}
              onMouseMove={handleDragMove1}
              onMouseUp={handleDragEnd1}
              onMouseLeave={handleDragEnd1}
              onTouchStart={handleDragStart1}
              onTouchMove={handleDragMove1}
              onTouchEnd={handleDragEnd1}
            >
              {duplicatedClients1.map((client, index) => (
                <motion.div
                  key={`${client.name}-${index}`}
                  className="flex-shrink-0 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-4 md:p-6 hover:bg-white/10 transition-all duration-300"
                  style={{
                    minWidth: '160px',
                    pointerEvents: 'auto'
                  }}
                  whileHover={{
                    y: -10,
                    scale: 1.05,
                    boxShadow: '0 20px 40px rgba(0,0,0,0.3)'
                  }}
                >
                  <div className="flex flex-col items-center space-y-3 md:space-y-4">
                    <div className="w-12 h-12 md:w-16 md:h-16 bg-white/10 rounded-xl flex items-center justify-center p-2 md:p-3">
                      <img
                        src={client.logo}
                        alt={client.name}
                        className="w-full h-full object-contain opacity-80 hover:opacity-100 transition-opacity duration-300"
                        draggable="false"
                      />
                    </div>
                    <h5 className="text-xs md:text-sm font-medium text-white text-center">{client.name}</h5>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </motion.div>
        {/* Segundo carrusel */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 1, ease: "easeOut", delay: 0.3 }}
        >
          <div className="overflow-hidden">
            <motion.div
              className="flex space-x-4 md:space-x-8 cursor-grab active:cursor-grabbing"
              style={{
                width: 'max-content',
                transform: `translateX(${currentPosition2}px)`,
                userSelect: 'none',
                touchAction: 'pan-y'
              }}
              onMouseDown={handleDragStart2}
              onMouseMove={handleDragMove2}
              onMouseUp={handleDragEnd2}
              onMouseLeave={handleDragEnd2}
              onTouchStart={handleDragStart2}
              onTouchMove={handleDragMove2}
              onTouchEnd={handleDragEnd2}
            >
              {duplicatedClients2.map((client, index) => (
                <motion.div
                  key={`${client.name}-${index}`}
                  className="flex-shrink-0 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-4 md:p-6 hover:bg-white/10 transition-all duration-300"
                  style={{
                    minWidth: '160px',
                    pointerEvents: 'auto'
                  }}
                  whileHover={{
                    y: -10,
                    scale: 1.05,
                    boxShadow: '0 20px 40px rgba(0,0,0,0.3)'
                  }}
                >
                  <div className="flex flex-col items-center space-y-3 md:space-y-4">
                    <div className="w-12 h-12 md:w-16 md:h-16 bg-white/10 rounded-xl flex items-center justify-center p-2 md:p-3">
                      <img
                        src={client.logo}
                        alt={client.name}
                        className="w-full h-full object-contain opacity-80 hover:opacity-100 transition-opacity duration-300"
                        draggable="false"
                      />
                    </div>
                    <h5 className="text-xs md:text-sm font-medium text-white text-center">{client.name}</h5>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </motion.div>
        {/* Estadísticas */}
        <motion.div
          className="mt-16 md:mt-20 text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.01 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <div className="grid grid-cols-3 gap-4 md:gap-12 max-w-4xl mx-auto">
            {(section.stats || []).map((stat, index) => (
              <motion.div
                key={index}
                className="text-center"
                whileHover={{ scale: 1.05 }}
              >
                <div className={`text-2xl md:text-5xl lg:text-6xl font-bold mb-1 md:mb-2 ${index % 2 ? 'text-purple-500' : 'text-pink-500'}`}>
                  <AnimatedCounter end={parseInt(stat.value, 10) || 0} duration={1.5} delay={0.02 * (index + 1)} suffix={stat.suffix || ''} />
                </div>
                <div className="text-gray-300 font-medium text-xs md:text-base">{t(stat.label)}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default BrandsSection;
